import React, { useState, useEffect } from 'react';
import { FileText, Plus, Zap } from 'lucide-react';
import { apiClient } from '../api/client';
import Modal from './ui/Modal';

interface Template {
  id: number;
  name: string;
  vendor_name?: string;
  usage_count: number;
  last_used?: string;
}

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (templateId: number | null) => void;
  onCreateTemplate: () => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  onCreateTemplate
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (templateId: number | null) => {
    setSelectedTemplateId(templateId);
  };

  const handleConfirm = () => {
    onSelectTemplate(selectedTemplateId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Read with a template?"
      description="Pick a template to read this invoice more accurately, or skip it"
      footerLeft={
        templates.length > 0 ? (
          <button
            onClick={() => { onSelectTemplate(null); onClose(); }}
            className="px-4 py-2 text-sm text-stone-500 dark:text-foreground-muted hover:text-stone-700 dark:hover:text-foreground transition-colors underline underline-offset-2"
          >
            Continue without template
          </button>
        ) : null
      }
      footer={
        templates.length > 0 ? (
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 text-stone-600 dark:text-coral-400 hover:text-stone-900 dark:hover:text-foreground transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-6 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors font-medium text-sm"
            >
              Continue
            </button>
          </>
        ) : null
      }
    >
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-coral-500/20 border-t-coral-500 rounded-full animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            /* No Templates - Suggest Creating One */
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-coral-100 dark:bg-coral-500/20 mx-auto mb-4 flex items-center justify-center">
                <FileText className="w-10 h-10 text-coral-600 dark:text-coral-400" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 dark:text-white mb-2">No templates yet</h3>
              <p className="text-stone-600 dark:text-foreground-secondary mb-6 max-w-md mx-auto">
                Add a sample invoice from a regular supplier and we'll read their future invoices more accurately.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => {
                    onClose();
                    onCreateTemplate();
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add a template
                </button>
                <button
                  onClick={() => {
                    onSelectTemplate(null);
                    onClose();
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-stone-100 dark:bg-coral-500/10 hover:bg-stone-200 dark:hover:bg-coral-500/20 text-stone-700 dark:text-foreground-muted border border-stone-200 dark:border-coral-500/30 rounded-lg transition-colors font-medium"
                >
                  <Zap className="w-4 h-4" />
                  Skip for now
                </button>
              </div>
              <p className="text-xs text-stone-500 dark:text-foreground-secondary mt-4">
                Invoices are read fine without a template — a template just makes a regular supplier's invoices more accurate.
              </p>
            </div>
          ) : (
            /* Templates Available */
            <div className="space-y-3">
              {/* Auto-detect option */}
              <div
                onClick={() => handleSelect(null)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedTemplateId === null
                    ? 'border-coral-500 bg-coral-500/10'
                    : 'border-stone-200 dark:border-coral-500/20 hover:border-coral-400 dark:hover:border-coral-500/40 hover:bg-coral-50 dark:hover:bg-coral-500/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-coral-100 to-blue-100 dark:from-coral-500/20 dark:to-blue-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-coral-600 dark:text-coral-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-stone-900 dark:text-white">Pick automatically</h3>
                    <p className="text-xs text-stone-600 dark:text-foreground-muted mt-0.5">
                      Let us choose the best matching template for this invoice
                    </p>
                  </div>
                  {selectedTemplateId === null && (
                    <div className="w-5 h-5 rounded-full bg-coral-500 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </div>
                  )}
                </div>
              </div>

              {/* Template list */}
              <div className="space-y-2">
                <p className="text-xs text-stone-500 dark:text-foreground-muted uppercase tracking-wide font-semibold px-1">
                  Or choose a specific template
                </p>
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleSelect(template.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTemplateId === template.id
                        ? 'border-coral-500 bg-coral-500/10'
                        : 'border-stone-200 dark:border-coral-500/20 hover:border-coral-400 dark:hover:border-coral-500/40 hover:bg-coral-50 dark:hover:bg-coral-500/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-coral-100 dark:bg-coral-500/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-coral-600 dark:text-coral-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-stone-900 dark:text-white">
                          {template.vendor_name || template.name}
                        </h3>
                        <p className="text-xs text-stone-500 dark:text-foreground-muted mt-0.5">
                          Used {template.usage_count} time{template.usage_count !== 1 ? 's' : ''}
                          {template.last_used && ` • Last used ${new Date(template.last_used).toLocaleDateString()}`}
                        </p>
                      </div>
                      {selectedTemplateId === template.id && (
                        <div className="w-5 h-5 rounded-full bg-coral-500 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Create New Template Option */}
              <div className="pt-3 border-t border-stone-200 dark:border-coral-500/20 mt-4">
                <p className="text-xs text-stone-500 dark:text-foreground-muted uppercase tracking-wide font-semibold px-1 mb-2">
                  Don't see your template?
                </p>
                <button
                  onClick={() => {
                    onClose();
                    onCreateTemplate();
                  }}
                  className="w-full p-4 rounded-lg border-2 border-dashed border-stone-300 dark:border-coral-500/30 hover:border-coral-400 dark:hover:border-coral-500/50 hover:bg-coral-50 dark:hover:bg-coral-500/5 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="text-sm font-semibold text-stone-900 dark:text-white">Add a new template</h3>
                      <p className="text-xs text-stone-500 dark:text-foreground-muted mt-0.5">
                        Upload a sample invoice from a new supplier
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
    </Modal>
  );
};

export default TemplateSelector;
